'use client';

import { useId, useMemo } from 'react';

import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useAuth } from '@shared/common/providers/AuthProvider';

import { ADS_CONSTANTS } from '../AdsConstants';
import { useAppendScript, useUserAdSettings } from '../hooks';
import styles from './StoreAppInlineTopCarouselBanner.module.scss';
import { StoreAppInlineTopCarouselSlide } from './StoreAppInlineTopCarouselSlide';

interface SlideConfig {
  id: string;
  dataKey: string;
  featureFlag: keyof ReturnType<typeof useUserAdSettings>;
}

const SLIDES_CONFIG: SlideConfig[] = [
  {
    id: `${ADS_CONSTANTS.storeAppInlineTopCarouselBanner1DataKey}-1`,
    dataKey: ADS_CONSTANTS.storeAppInlineTopCarouselBanner1DataKey,
    featureFlag: 'showStoreAppInlineTopCarouselBanner1',
  },
  {
    id: `${ADS_CONSTANTS.storeAppInlineTopCarouselBanner1DataKey}-2`,
    dataKey: ADS_CONSTANTS.storeAppInlineTopCarouselBanner2DataKey,
    featureFlag: 'showStoreAppInlineTopCarouselBanner2',
  },
  {
    id: `${ADS_CONSTANTS.storeAppInlineTopCarouselBanner1DataKey}-3`,
    dataKey: ADS_CONSTANTS.storeAppInlineTopCarouselBanner3DataKey,
    featureFlag: 'showStoreAppInlineTopCarouselBanner3',
  },
];

export const StoreAppInlineTopCarouselBanner = () => {
  const toggles = useUserAdSettings();
  const enabledSlides = useMemo(
    () => SLIDES_CONFIG.filter((slide) => Boolean(toggles[slide.featureFlag])),
    [toggles],
  );

  const { user } = useAuth();
  const id = useId();
  const userId = user?.id;

  useAppendScript({
    id,
    userId,
  });

  if (enabledSlides.length === 0) {
    return null;
  }

  if (enabledSlides.length === 1) {
    return (
      <div id={id} className={styles.container}>
        <StoreAppInlineTopCarouselSlide userId={userId} dataKey={enabledSlides[0].dataKey} />
      </div>
    );
  }

  return (
    <Swiper
      modules={[Autoplay, Pagination]}
      loop
      watchOverflow
      autoplay={{
        delay: 10000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      }}
      spaceBetween={8}
      speed={500}
      slidesPerView="auto"
      centeredSlides={false}
      pagination={{
        clickable: true,
      }}
      className={styles.container}
    >
      {enabledSlides.map((slide, index) => (
        <SwiperSlide key={slide.dataKey} id={slide.id}>
          <StoreAppInlineTopCarouselSlide userId={userId} dataKey={slide.dataKey} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
